import { AxiosPromise } from 'axios';

import { PolicyTypeEnum } from '../pages/Policy/utils/constants';
import {
  GlobalSettingFeatureShow,
  NotificationEmailSettingsResDto,
} from '../types';
import {
  ApprovedHeaderFieldsItem,
  CreateRecipientGroupReqDto,
  DeleteApprovedUsersDto,
  DeleteHighProfileDomainsProps,
  DNSExceptionProps,
  HighProfileDomainsProps,
  InputPasswordExtractionRulesLists,
  TestPasswordExtractionRulesLists,
  HighProfileUsersDto,
  PLMExceptionProps,
  TimeOfClickStatusReqDto,
  UpdateRecipientGroupReqDto,
  ExchangeInlineProtectionReqDto,
  ConditionalAccessSaveProps,
  AddPLMExceptionProps,
  ChangeUserConditionalAccessProps,
} from '../types/globalSetting';
import request from '../utils/request';
import {
  ExchangeInlineProtectionSettings,
  GmailInlineProtectionInfo,
  OtherExchangeInlineProtectionSettings,
  OtherGmailInlineProtectionSettings,
} from '../pages/GlobalSetting/utils/types';
import { AuthenticationInfoRes } from '../types/provision';
import { SsoConfigDto } from '@Types/administrator';

function getPromotionBanner() {
  return request({
    method: 'get',
    url: `/promotionBanner`,
  });
}

function getLiveDashboard() {
  return request({
    method: 'get',
    url: `/livedDashboard`,
  });
}

function getNotification() {
  return request({
    method: 'get',
    url: `/notification`,
  });
}

function getLicense() {
  return request({
    method: 'get',
    url: `/company/license`,
    headers: { 'x-org-id': 'all' },
  });
}

function getSsoConfig(): AxiosPromise<SsoConfigDto> {
  return request({
    method: 'get',
    url: `/company/ssoConfig`,
  });
}

function updateSsoConfig(ssoConfig: SsoConfigDto) {
  return request({
    method: 'put',
    url: `/company/ssoConfig`,
    data: ssoConfig,
  });
}

function getSelectedGlobalSettingFeatureShow(
  orgId: string
): AxiosPromise<GlobalSettingFeatureShow> {
  return request({
    method: 'get',
    url: `/globalSettings/categories/featureShow`,
    headers: { 'x-org-id': orgId },
  });
}

function getGroupGlobalSettingFeatureShow(): AxiosPromise<GlobalSettingFeatureShow> {
  return request({
    method: 'get',
    url: `/globalSettings/categories/featureShow`,
    headers: { 'x-org-id': 'all' },
  });
}

function getOrgGlobalSettingFeatureShow(
  orgId: string
): AxiosPromise<GlobalSettingFeatureShow> {
  return request({
    method: 'get',
    url: `/globalSettings/categories/featureShow`,
    headers: { 'x-org-id': orgId },
  });
}

function getGlobalSettings(orgId: 'all' | (string & {})) {
  if (orgId === 'all') {
    return getGroupGlobalSettingFeatureShow();
  } else {
    return getOrgGlobalSettingFeatureShow(orgId);
  }
}

function getPermissions() {
  return request({
    method: 'get',
    url: `/company/permissions`,
    headers: { 'x-org-id': 'all' },
  });
}
function getInternalDomains() {
  return request({
    method: 'get',
    url: `/globalSettings/internalDomains`,
  });
}

function addInternalDomains(internalDomains: string[]) {
  return request({
    method: 'post',
    url: `/globalSettings/internalDomains`,
    data: {
      internalDomains,
    },
  });
}

function deleteInternalDomains(internalDomains: string[]) {
  return request({
    method: 'delete',
    url: `/globalSettings/internalDomains`,
    data: {
      internalDomains,
    },
  });
}

function getHighProfileUserExceptionList() {
  return request({
    method: 'get',
    url: `/globalSettings/highProfileExceptionUserList`,
  });
}

function addHighProfileUserExceptionList(users: DNSExceptionProps) {
  return request({
    method: 'post',
    url: `/globalSettings/highProfileExceptionUserList`,
    data: users,
  });
}

function deleteHighProfileUserExceptionList(users: DNSExceptionProps) {
  return request({
    method: 'delete',
    url: `/globalSettings/highProfileExceptionUserList`,
    data: users,
  });
}

function getNotificationEmailSettings() {
  return request({
    method: 'get',
    url: `/globalSettings/notificationEmailSettings`,
  });
}

function updateNotificationEmailSettings(
  notificationEmailSettingsResDto: NotificationEmailSettingsResDto
) {
  return request({
    method: 'put',
    url: `/globalSettings/notificationEmailSettings`,
    data: notificationEmailSettingsResDto,
  });
}

function getRecipientGroups() {
  return request({
    method: 'get',
    url: `/globalSettings/recipientGroups`,
  });
}

function getRecipientGroupsForPolicy() {
  return request({
    method: 'get',
    url: `/globalSettings/recipientGroups/types/0`,
  });
}

function addRecipientGroups(
  createRecipientGroupReqDto: CreateRecipientGroupReqDto
) {
  return request({
    method: 'post',
    url: `/globalSettings/recipientGroups`,
    data: createRecipientGroupReqDto,
  });
}

function updateRecipientGroups(
  id: string,
  updateRecipientGroupReqDto: UpdateRecipientGroupReqDto
) {
  return request({
    method: 'put',
    url: `/globalSettings/recipientGroups/${id}`,
    data: updateRecipientGroupReqDto,
  });
}

function deleteRecipientGroups(
  id: string,
  updateRecipientGroupReqDto: UpdateRecipientGroupReqDto
) {
  return request({
    method: 'delete',
    url: `/globalSettings/recipientGroups/${id}`,
    data: updateRecipientGroupReqDto,
  });
}

function applyRecipientGroupToAllPolicies(id: string) {
  return request({
    method: 'post',
    url: `/globalSettings/recipientGroups/${id}/applyToAllPolicies`,
  });
}

function getApprovedUsers(serviceType: PolicyTypeEnum) {
  return request({
    method: 'get',
    url: `/globalSettings/services/${serviceType}/approvedUsers`,
  });
}

function addApprovedUsers(
  serviceType: PolicyTypeEnum,
  users: DeleteApprovedUsersDto[]
) {
  return request({
    method: 'post',
    url: `/globalSettings/services/${serviceType}/approvedUsers`,
    data: {
      users,
    },
  });
}

function deleteApprovedUsers(
  serviceType: PolicyTypeEnum,
  users: DeleteApprovedUsersDto[]
) {
  return request({
    method: 'delete',
    url: `/globalSettings/services/${serviceType}/approvedUsers`,
    data: {
      users,
    },
  });
}

function getPLMExceptionList() {
  return request({
    method: 'get',
    url: `/globalSettings/predictiveMachineLearningExceptionList`,
  });
}

function addPLMExceptionList(data: AddPLMExceptionProps) {
  return request({
    method: 'post',
    url: `/globalSettings/predictiveMachineLearningExceptionList`,
    data,
  });
}

function deletePLMExceptionList(data: PLMExceptionProps[]) {
  return request({
    method: 'delete',
    url: `/globalSettings/predictiveMachineLearningExceptionList`,
    data,
  });
}

function getDNSExceptionList() {
  return request({
    method: 'get',
    url: `/globalSettings/nameSpoofingDetectionExceptionList`,
  });
}

function addDNSExceptionList(users: DNSExceptionProps) {
  return request({
    method: 'post',
    url: `/globalSettings/nameSpoofingDetectionExceptionList`,
    data: users,
  });
}

function deleteDNSExceptionList(users: DNSExceptionProps) {
  return request({
    method: 'delete',
    url: `/globalSettings/nameSpoofingDetectionExceptionList`,
    data: users,
  });
}

function getHighProfileUsers() {
  return request({
    method: 'get',
    url: `/globalSettings/highProfileUsers`,
  });
}

function addHighProfileUsers(users: HighProfileUsersDto[]) {
  return request({
    method: 'post',
    url: `/globalSettings/highProfileUsers`,
    data: {
      users,
    },
  });
}

function deleteHighProfileUsers(users: HighProfileUsersDto[]) {
  return request({
    method: 'delete',
    url: `/globalSettings/highProfileUsers`,
    data: {
      users,
    },
  });
}

function getHighProfileDomain() {
  return request({
    method: 'get',
    url: `/globalSettings/highProfileDomains`,
  });
}

function updateHighProfileDomain(
  highProfileDomainsProps: HighProfileDomainsProps
) {
  return request({
    method: 'put',
    url: `/globalSettings/highProfileDomains`,
    data: highProfileDomainsProps,
  });
}

function deleteHighProfileDomain(
  highProfileDomainsProps: DeleteHighProfileDomainsProps
) {
  return request({
    method: 'delete',
    url: `/globalSettings/highProfileDomains`,
    data: highProfileDomainsProps,
  });
}

function getApprovedHeaderFields(serviceType: PolicyTypeEnum) {
  return request({
    method: 'get',
    url: `/globalSettings/services/${serviceType}/approvedHeaders`,
  });
}

function addApprovedHeaderFields(
  serviceType: PolicyTypeEnum,
  approvedHeaderFieldsItem: ApprovedHeaderFieldsItem
) {
  return request({
    method: 'post',
    url: `/globalSettings/services/${serviceType}/approvedHeaders`,
    data: approvedHeaderFieldsItem,
  });
}

function deleteApprovedHeaderFields(
  serviceType: PolicyTypeEnum,
  approvedHeaderFieldsItem: ApprovedHeaderFieldsItem
) {
  return request({
    method: 'delete',
    url: `/globalSettings/services/${serviceType}/approvedHeaders`,
    data: approvedHeaderFieldsItem,
  });
}

function switchApprovedHeaderFields(
  serviceType: PolicyTypeEnum,
  enabledStatus: boolean
) {
  return request({
    method: 'patch',
    url: `/globalSettings/services/${serviceType}/approvedHeaders/enabledStatus`,
    data: {
      enabledStatus,
    },
  });
}

function getBlockLists(serviceType: PolicyTypeEnum) {
  return request({
    method: 'get',
    url: `/globalSettings/services/${serviceType}/blockedLists`,
  });
}

function getSuspiciousObjectSettingsEnabledStatus() {
  return request({
    method: 'get',
    url: `/globalSettings/suspiciousObjects/enabledStatus`,
  });
}

function updateSuspiciousObjectSettingsEnabledStatus(enabledStatus: boolean) {
  return request({
    method: 'put',
    url: `/globalSettings/suspiciousObjects/enabledStatus`,
    data: {
      enabledStatus,
    },
  });
}

function getSocialGraphSettingsEnabledStatus() {
  return request({
    method: 'get',
    url: `/globalSettings/socialGraph/enabledStatus`,
  });
}

function switchBlockLists(serviceType: PolicyTypeEnum, enabledStatus: boolean) {
  return request({
    method: 'patch',
    url: `/globalSettings/services/${serviceType}/blockedLists/enabledStatus`,
    data: {
      enabledStatus,
    },
  });
}

function getPasswordExtractionRules() {
  return request({
    method: 'get',
    url: `/globalSettings/passwordExtractionRules`,
  });
}

function addPasswordExtractionRules(
  passwordExtractionRules: InputPasswordExtractionRulesLists
) {
  return request({
    method: 'post',
    url: `/globalSettings/passwordExtractionRules`,
    data: passwordExtractionRules,
  });
}

function editPasswordExtractionRules(
  passwordExtractionRules: InputPasswordExtractionRulesLists
) {
  return request({
    method: 'post',
    url: `/globalSettings/passwordExtractionRules`,
    data: passwordExtractionRules,
  });
}

function deletePasswordExtractionRules(
  passwordExtractionRules: InputPasswordExtractionRulesLists
) {
  return request({
    method: 'post',
    url: `/globalSettings/passwordExtractionRules/batchDelete`,
    data: passwordExtractionRules,
  });
}
function testPasswordExtractionRules(
  passwordExtractionRules: TestPasswordExtractionRulesLists
) {
  return request({
    method: 'post',
    url: `/globalSettings/passwordExtractionRules/test`,
    data: passwordExtractionRules,
  });
}
function updatePasswordExtractionRulesEnabledStatus(
  enablePasswordExtraction: Number
) {
  return request({
    method: 'post',
    url: `/globalSettings/passwordExtractionRulesSwitch`,
    data: {
      enablePasswordExtraction,
    },
  });
}
function getTimeOfClickStatus() {
  return request({
    method: 'get',
    url: `/globalSettings/clickTimeProtection/configuration`,
  });
}

function updateTimeOfClickStatus(timeOfClickStatus: TimeOfClickStatusReqDto) {
  return request({
    method: 'post',
    url: `/globalSettings/clickTimeProtection/configuration`,
    data: {
      ...timeOfClickStatus,
    },
  });
}

function getTeamsChatModelList(): AxiosPromise<any> {
  return request({
    method: 'get',
    url: `/globalSettings/services/teamsChat/licensingModelSettings`,
  });
}
function getTeamsChatAppId(): AxiosPromise<any> {
  return request({
    method: 'get',
    url: `/globalSettings/services/teamsChat/appId`,
  });
}
function updateTeamsChatLicensingModelSettings(licensingModel: string) {
  return request({
    method: 'post',
    url: `/globalSettings/services/teamsChat/licensingModelSettings`,
    data: {
      licensingModel,
    },
  });
}

function setExchangeInlineProtectionStatus(
  exchangeInlineProtectionReqDto: ExchangeInlineProtectionReqDto
): AxiosPromise<any> {
  return request({
    method: 'post',
    url: `/globalSettings/services/exchangeInline/inlineProtectionSettings`,
    data: {
      ...exchangeInlineProtectionReqDto,
    },
    timeout: 120000,
  });
}

function getExchangeInlineProtectionStatus(): AxiosPromise<ExchangeInlineProtectionSettings> {
  return request({
    method: 'get',
    url: `/globalSettings/services/exchangeInline/protectionSettings`,
  });
}

function setExchangeInlineOtherProtectionSettings(
  otherExchangeInlineProtectionSettings: OtherExchangeInlineProtectionSettings
) {
  return request({
    method: 'post',
    url: `/globalSettings/services/exchangeInline/otherProtectionSettings`,
    data: {
      ...otherExchangeInlineProtectionSettings,
    },
  });
}

function getGmailInlineProtectionSettings(): AxiosPromise<OtherGmailInlineProtectionSettings> {
  return request({
    method: 'get',
    url: `/globalSettings/services/gmailInline/protectionSettings`,
  });
}

function setGmailInlineProtectionSettings(
  gmailInlineProtectionSettings: OtherGmailInlineProtectionSettings
) {
  return request({
    method: 'post',
    url: `/globalSettings/services/gmailInline/protectionSettings`,
    data: {
      ...gmailInlineProtectionSettings,
    },
  });
}

function getGmailInlineProtectionInfo(): AxiosPromise<GmailInlineProtectionInfo> {
  return request({
    method: 'get',
    url: `/org/gmailInline/protectionInfo`,
  });
}

function getConditionalAccessPolicy() {
  return request({
    method: 'get',
    url: `/globalSettings/microsoftIdentityProtection/conditionalAccessPolicy`,
  });
}

function getConditionalAccessUsers(userEmail: string) {
  return request({
    method: 'get',
    url: `/globalSettings/microsoftIdentityProtection/conditionalAccessPolicy/users?userEmail=${userEmail}`,
  });
}

function saveConditionalAccessPolicy(
  policyId: string,
  conditionalAccessProps: ConditionalAccessSaveProps
) {
  return request({
    method: 'patch',
    url: `/globalSettings/microsoftIdentityProtection/conditionalAccessPolicy/policies/${policyId}`,
    data: {
      ...conditionalAccessProps,
    },
  });
}

function changeUserConditionalAccessPolicy(
  userPrincipalName: string,
  changeUserConditionalAccessProps: ChangeUserConditionalAccessProps
) {
  return request({
    method: 'post',
    url: `globalSettings/microsoftIdentityProtection/conditionalAccessPolicy/users/${userPrincipalName}/changePolicy`,
    data: {
      ...changeUserConditionalAccessProps,
    },
  });
}

function getWhiteLabel(tenantId: string) {
  return request({
    method: 'get',
    url: `/whiteLabel/${tenantId}`,
  });
}

export interface HpuThresholdSettings {
  hpuThresholdOption: number; // 0: built-in, 1: customize
  hpuThresholdLevel: number; // 0: normal, 1: aggressive, 2: conservative
}

function getHpuThresholdSettings(): AxiosPromise<HpuThresholdSettings> {
  return request({
    method: 'get',
    url: `/globalSettings/categories/hpuThreshold`,
  });
}

function updateHpuThresholdSettings(settings: HpuThresholdSettings) {
  return request({
    method: 'put',
    url: `/globalSettings/categories/hpuThreshold`,
    data: settings,
  });
}

//***********************************************************************************************************************
//* Notification Mailboxes apis
//***********************************************************************************************************************
function deleteNotificationMailboxes(data: {
  companyGuid: string;
  usableRange: number;
  mailboxes: string[];
}) {
  return request({
    method: 'post',
    url: `/notificationMailbox`,
    data,
  });
}

function getNotificationMailboxes() {
  return request({
    method: 'get',
    url: `/notificationMailbox`,
  });
}

function getNotificationMailboxesProvisionAuthInfo(): AxiosPromise<AuthenticationInfoRes> {
  return request({
    method: 'get',
    url: `/apps/graphNotification/provision/authentications`,
    headers: {
      'x-org-id': 'all',
    },
  });
}

function saveNotificationMailboxesProvisionInfo(code: string) {
  return request({
    method: 'post',
    url: `/apps/graphNotification/provision/initToken`,
    headers: {
      'x-org-id': 'all',
    },
    data: {
      authCode: code,
    },
  });
}

//***********************************************************************************************************************
//* Calendar Event Settings (Exchange Online) apis
//***********************************************************************************************************************
function getCalendarEventPermission(): AxiosPromise<{
  hasPermission: boolean;
}> {
  return request({
    method: 'get',
    url: `/globalSettings/services/exchange/calendarEvents/permissions`,
  });
}

function getCalendarEventSettings(): AxiosPromise<{
  enabled: boolean;
  action: 'disclaimer' | 'delete';
  disclaimerContent: string;
}> {
  return request({
    method: 'get',
    url: `/globalSettings/services/exchange/calendarEvents/settings`,
  });
}

function updateCalendarEventSettings(data: {
  enabled: boolean;
  action: 'disclaimer' | 'delete';
  disclaimerContent: string;
}): AxiosPromise<{ status: number; message: string }> {
  return request({
    method: 'post',
    url: `/globalSettings/services/exchange/calendarEvents/settings`,
    data,
  });
}

function getMisdirectedEmailDetectionSettings(): AxiosPromise<{
  enabled: boolean;
  action: 'prompt' | 'pass';
}> {
  return request({
    method: 'get',
    url: `/globalSettings/services/exchange/misdirectedEmailDetection/settings`,
  });
}

function updateMisdirectedEmailDetectionSettings(data: {
  enabled: boolean;
  action: 'prompt' | 'pass';
}): AxiosPromise<{ status: number; message: string }> {
  return request({
    method: 'put',
    url: `/globalSettings/services/exchange/misdirectedEmailDetection/settings`,
    data,
  });
}

// Smart Alert add-in admin-consent banner (Misdirected Email Detection page).
// `state` round-trips to AAD and back into BFF's /ack callback — the FE only
// needs to navigate the admin to `url` and then poll /status.
function getSmartAlertAdminConsentUrl(): AxiosPromise<{
  url: string;
  state: string;
  tenantId: string;
}> {
  return request({
    method: 'get',
    url: `/smartAlert/adminConsent/url`,
  });
}

function getSmartAlertAdminConsentStatus(): AxiosPromise<{
  consented: boolean;
  consentedAt?: string;
}> {
  return request({
    method: 'get',
    url: `/smartAlert/adminConsent/status`,
  });
}

// Called by AdminConsentBanner after the same-origin /admin-consent.html
// page writes an `approved` outcome to localStorage. The banner has the
// authenticated session and sends the Microsoft callback tenant + signed state;
// BFF verifies both before recording consent for the org.
function postSmartAlertAdminConsentAck(args: {
  orgId: string;
  tenantId: string;
  state: string;
}): AxiosPromise<{ success: true }> {
  return request({
    method: 'post',
    url: `/smartAlert/adminConsent/ack`,
    data: args,
  });
}

export default {
  getPromotionBanner,
  getLiveDashboard,
  getNotification,
  getLicense,
  getSsoConfig,
  updateSsoConfig,
  getSelectedGlobalSettingFeatureShow,
  getGlobalSettings,
  getPermissions,
  getInternalDomains,
  addInternalDomains,
  deleteInternalDomains,
  getHighProfileUserExceptionList,
  addHighProfileUserExceptionList,
  deleteHighProfileUserExceptionList,
  getNotificationEmailSettings,
  updateNotificationEmailSettings,
  getRecipientGroups,
  getRecipientGroupsForPolicy,
  addRecipientGroups,
  updateRecipientGroups,
  deleteRecipientGroups,
  applyRecipientGroupToAllPolicies,
  getApprovedUsers,
  addApprovedUsers,
  deleteApprovedUsers,
  getPLMExceptionList,
  addPLMExceptionList,
  deletePLMExceptionList,
  getDNSExceptionList,
  addDNSExceptionList,
  deleteDNSExceptionList,
  getHighProfileUsers,
  addHighProfileUsers,
  deleteHighProfileUsers,
  getHighProfileDomain,
  updateHighProfileDomain,
  deleteHighProfileDomain,
  getApprovedHeaderFields,
  addApprovedHeaderFields,
  deleteApprovedHeaderFields,
  switchApprovedHeaderFields,
  getBlockLists,
  getSuspiciousObjectSettingsEnabledStatus,
  updateSuspiciousObjectSettingsEnabledStatus,
  switchBlockLists,
  getPasswordExtractionRules,
  addPasswordExtractionRules,
  editPasswordExtractionRules,
  deletePasswordExtractionRules,
  testPasswordExtractionRules,
  updatePasswordExtractionRulesEnabledStatus,
  getTimeOfClickStatus,
  updateTimeOfClickStatus,
  getTeamsChatModelList,
  getTeamsChatAppId,
  updateTeamsChatLicensingModelSettings,
  setExchangeInlineProtectionStatus,
  getExchangeInlineProtectionStatus,
  setExchangeInlineOtherProtectionSettings,
  getGmailInlineProtectionSettings,
  setGmailInlineProtectionSettings,
  getGmailInlineProtectionInfo,
  getConditionalAccessPolicy,
  getConditionalAccessUsers,
  saveConditionalAccessPolicy,
  changeUserConditionalAccessPolicy,
  getWhiteLabel,
  deleteNotificationMailboxes,
  getNotificationMailboxes,
  getNotificationMailboxesProvisionAuthInfo,
  saveNotificationMailboxesProvisionInfo,
  getSocialGraphSettingsEnabledStatus,
  getHpuThresholdSettings,
  updateHpuThresholdSettings,
  getCalendarEventPermission,
  getCalendarEventSettings,
  updateCalendarEventSettings,
  getMisdirectedEmailDetectionSettings,
  updateMisdirectedEmailDetectionSettings,
  getSmartAlertAdminConsentUrl,
  getSmartAlertAdminConsentStatus,
  postSmartAlertAdminConsentAck,
};
